import { gql } from 'graphql-tag';

// Authentication-related operations against the Nexus backend
// Keep this as the single source of truth to ensure DRY gql documents

export const SIGN_IN_MUTATION = gql`
  mutation AuthSignIn($input: SignInInput!) {
    authSignIn(input: $input) {
      accessToken
      refreshToken
      idToken
      expiresIn
      user {
        id
        userId
        email
        firstName
        lastName
        fullName
        profileImg
        emailVerified
        phoneNumber
        phoneVerified
        userTypes
      }
      success
      message
      otpSent
    }
  }
`;

export const VERIFY_OTP_MUTATION = gql`
  mutation AuthVerifyOtp($input: VerifyOtpInput!) {
    authVerifyOtp(input: $input) {
      accessToken
      refreshToken
      idToken
      expiresIn
      user {
        id
        userId
        email
        firstName
        lastName
        fullName
        profileImg
        emailVerified
        phoneNumber
        phoneVerified
        userTypes
      }
      success
      message
    }
  }
`;

export const SIGN_UP_MUTATION = gql`
  mutation AuthSignUp($input: SignUpInput!) {
    authSignUp(input: $input) {
      accessToken
      refreshToken
      idToken
      expiresIn
      user {
        id
        userId
        email
        firstName
        lastName
        fullName
        profileImg
        emailVerified
        phoneNumber
        phoneVerified
        userTypes
      }
      success
      message
    }
  }
`;

export const CHECK_USER_EXISTS_QUERY = gql`
  query AuthCheckUserExists($input: CheckUserExistsInput!) {
    authCheckUserExists(input: $input)
  }
`;

export const CHANGE_PASSWORD_MUTATION = gql`
  mutation AuthChangePassword($input: ChangePasswordInput!) {
    authChangePassword(input: $input) {
      accessToken
      refreshToken
      idToken
      expiresIn
      user {
        id
        userId
        email
        firstName
        lastName
        fullName
        profileImg
        emailVerified
        phoneNumber
        phoneVerified
        userTypes
      }
      success
      message
    }
  }
`;

export const VERIFY_JWT_MUTATION = gql`
  mutation AuthVerifyJwt($input: VerifyJwtInput!) {
    authVerifyJwt(input: $input) {
      success
      message
      user {
        id
        userId
        email
        firstName
        lastName
        fullName
        profileImg
        emailVerified
        phoneNumber
        phoneVerified
        userTypes
      }
    }
  }
`;

export const FORGOT_PASSWORD_MUTATION = gql`
  mutation AuthForgotPassword($input: ForgotPasswordInput!) {
    authForgotPassword(input: $input) {
      success
      message
    }
  }
`;

// Current authenticated user details
export const ME_QUERY = gql`
  query Me {
    me {
      dbUser {
        id
        slug
        portfolioPublic
      }
      userId
      username
      email
      firstName
      lastName
      fullName
      profileImg
      profile {
        small
        medium
        large
      }
      emailVerified
      phoneNumber
      phoneVerified
      shareCode
      userTypes
      stripeConnectId
      stripeVerified
    }
  }
`;

// Check whether the currently authenticated user's portfolio is complete
export const IS_PORTFOLIO_COMPLETE_QUERY = gql`
  query AuthIsPortfolioComplete {
    authIsPortfolioComplete
  }
`;

// Refresh tokens silently (cookie-based)
export const REFRESH_MUTATION = gql`
  mutation AuthRefresh {
    authRefresh {
      ok
      accessToken
      refreshToken
    }
  }
`;

// Task 4: Password Addition to OTP Accounts
// Add password authentication to existing OTP account
export const ADD_PASSWORD_TO_ACCOUNT_MUTATION = gql`
  mutation AuthAddPasswordToAccount($input: AddPasswordToAccountInput!) {
    authAddPasswordToAccount(input: $input) {
      success
      message
      identities {
        connection
        provider
        userId
        isSocial
      }
    }
  }
`;

// Check available authentication methods for a user
export const CHECK_AUTH_METHODS_QUERY = gql`
  query AuthCheckAuthMethods($input: CheckAuthMethodsInput!) {
    authCheckAuthMethods(input: $input) {
      hasPasswordAuth
      hasEmailOtp
      hasPhoneOtp
      availableMethods
      identities {
        connection
        provider
        userId
        isSocial
      }
    }
  }
`;

// Resend OTP for verification
export const RESEND_OTP_MUTATION = gql`
  mutation AuthResendOtp($input: ResendOtpInput!) {
    authResendOtp(input: $input) {
      success
      message
      otpSent
      otpAlreadySent
      resendCooldownMs
      nextResendAllowedAt
    }
  }
`;

// Check merge eligibility for identity linking
export const CHECK_MERGE_ELIGIBILITY_QUERY = gql`
  query AuthCheckMergeEligibility($input: CheckMergeEligibilityInput!) {
    authCheckMergeEligibility(input: $input) {
      exists
      canMerge
      conflictUserId
      conflictType
      message
      hasEmail
      hasPhone
    }
  }
`;

// Link email to account
export const LINK_EMAIL_MUTATION = gql`
  mutation AuthLinkEmail($input: LinkEmailInput!) {
    authLinkEmail(input: $input) {
      conflict
      conflictUserId
      canMerge
      message
      requiresVerification
      success
      mergedAccountId
    }
  }
`;

// Link phone to account
export const LINK_PHONE_MUTATION = gql`
  mutation AuthLinkPhone($input: LinkPhoneInput!) {
    authLinkPhone(input: $input) {
      conflict
      conflictUserId
      canMerge
      message
      requiresVerification
      success
      mergedAccountId
    }
  }
`;

// Migrate identity between accounts
export const MIGRATE_IDENTITY_MUTATION = gql`
  mutation AuthMigrateIdentity($input: MigrateIdentityInput!) {
    authMigrateIdentity(input: $input) {
      message
      success
    }
  }
`;

// Upload profile image (proxied through Nexus to avoid CORS)
export const UPLOAD_PROFILE_IMAGE_MUTATION = gql`
  mutation UploadProfileImage($input: UploadProfileImageInput!) {
    uploadProfileImage(input: $input) {
      ok
      message
      profile {
        small
        medium
        large
      }
    }
  }
`;

// Remove profile image
export const REMOVE_PROFILE_IMAGE_MUTATION = gql`
  mutation RemoveProfileImage {
    removeProfileImage {
      ok
      message
    }
  }
`;

// ============================================================
// Activity-Level OTP Verification
// These mutations verify email/phone access WITHOUT modifying
// Auth0 or User records. Used for activity-level verification
// where we need to prove access to an identifier without
// linking it to the user's account.
// ============================================================

// Send activity-level email OTP
export const SEND_ACTIVITY_EMAIL_OTP_MUTATION = gql`
  mutation SendActivityEmailOtp($input: SendActivityEmailOtpInput!) {
    sendActivityEmailOtp(input: $input) {
      success
      message
      sent
      identifier
      remainingAttempts
    }
  }
`;

// Verify activity-level email OTP
export const VERIFY_ACTIVITY_EMAIL_OTP_MUTATION = gql`
  mutation VerifyActivityEmailOtp($input: VerifyActivityEmailOtpInput!) {
    verifyActivityEmailOtp(input: $input) {
      success
      message
      verified
      identifier
      verifiedAt
      remainingAttempts
    }
  }
`;

// Send activity-level phone OTP
export const SEND_ACTIVITY_PHONE_OTP_MUTATION = gql`
  mutation SendActivityPhoneOtp($input: SendActivityPhoneOtpInput!) {
    sendActivityPhoneOtp(input: $input) {
      success
      message
      sent
      identifier
      remainingAttempts
    }
  }
`;

// Verify activity-level phone OTP
export const VERIFY_ACTIVITY_PHONE_OTP_MUTATION = gql`
  mutation VerifyActivityPhoneOtp($input: VerifyActivityPhoneOtpInput!) {
    verifyActivityPhoneOtp(input: $input) {
      success
      message
      verified
      identifier
      verifiedAt
      remainingAttempts
    }
  }
`;
